import { t, useStoreLanguage } from "../features/store-language/i18n.ts";
import React, { useEffect, useRef, useState } from "react";
import DirectionalIcon from "../features/navigation/DirectionalIcon.tsx";
import { CaretDown, X } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import "../components/Shop/Category/CategoryNavigation.css";

export interface ShopTopMenuItem {
  key: string;
  label: string;
  path: string;
  children?: ShopTopMenuItem[];
}

interface ShopTopMenuProps {
  open: boolean;
  items: ShopTopMenuItem[];
  currentPath: string;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

function containsPath(item: ShopTopMenuItem, path: string): boolean {
  return item.path === path || Boolean(item.children?.some(function (child) { return containsPath(child, path); }));
}

function CategoryBranch({ item, currentPath, onClose }: {
  item: ShopTopMenuItem;
  currentPath: string;
  onClose: () => void;
}) {
  useStoreLanguage();
  const [expanded, setExpanded] = useState(function () { return containsPath(item, currentPath); });
  const hasChildren = Boolean(item.children?.length);
  return (
    <li className="shop-category-branch">
      <div className="shop-category-branch__row">
        <Link
          to={item.path}
          aria-current={currentPath === item.path ? "page" : undefined}
          onClick={onClose}
        >
          {item.label}
        </Link>
        {hasChildren ? (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={`category-branch-${item.key}`}
            onClick={function () { setExpanded(!expanded); }}
          >
            <span>{expanded ? t("بستن زیر‌دسته‌ها") : t("زیر‌دسته‌ها")}</span>
            <CaretDown size={18} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {hasChildren && expanded ? (
        <ul id={`category-branch-${item.key}`}>
          {item.children?.map(function (child) {
            return (
              <CategoryBranch
                key={child.key}
                item={child}
                currentPath={currentPath}
                onClose={onClose}
              />
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}

export default function ShopTopMenu({
  open,
  items,
  currentPath,
  onClose,
  triggerRef,
}: ShopTopMenuProps) {
  const storeLanguage = useStoreLanguage();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  useEffect(function () {
    if (!open) return undefined;
    const trigger = triggerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          "a[href], button:not([disabled])",
        ) || [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return function () {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      trigger?.focus();
    };
  }, [storeLanguage, onClose, open, triggerRef]);

  useEffect(function () {
    if (open) setExpandedKey(items.find(function (item) { return containsPath(item, currentPath); })?.key ?? "categories");
    else setExpandedKey(null);
  }, [storeLanguage, open, items, currentPath]);

  if (!open) return null;

  return (
    <>
    <div
      className="shop-top-menu-backdrop"
      aria-hidden="true"
      onClick={onClose}
    />
    <div
      id="shop-top-menu"
      className="shop-top-menu"
      role="dialog"
      aria-modal="true"
      aria-label={t("منوی اصلی کاتالوگ")}
      ref={dialogRef}
    >
      <div className="shop-top-menu__header">
        <h2 className="shop-top-menu__title">{t("دسته‌بندی محصولات")}</h2>
        <button
          type="button"
          className="shop-top-menu__close"
          aria-label={t("بستن منوی کاتالوگ")}
          ref={closeRef}
          onClick={onClose}
        >
          <X size={25} aria-hidden="true" />
        </button>
      </div>

      <nav className="shop-top-menu__nav" aria-label={t("بخش‌های کاتالوگ")}>
        {items.map(function (item) {
          const hasChildren = Boolean(item.children?.length);
          const expanded = expandedKey === item.key;
          const active = containsPath(item, currentPath);
          return (
            <section
              className={`shop-top-menu__section${active ? " is-active" : ""}`}
              key={item.key}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className="shop-top-menu__row"
                  aria-expanded={expanded}
                  aria-controls={`shop-top-menu-panel-${item.key}`}
                  onClick={function () {
                    setExpandedKey(expanded ? null : item.key);
                  }}
                >
                  <span>{item.label}</span>
                  <CaretDown
                    size={20}
                    aria-hidden="true"
                    className={expanded ? "is-expanded" : ""}
                  />
                </button>
              ) : (
                <Link
                  to={item.path}
                  className="shop-top-menu__row"
                  aria-current={active ? "page" : undefined}
                  onClick={onClose}
                >
                  <span>{item.label}</span>
                  <DirectionalIcon
                    direction="forward"
                    variant="chevron"
                    size={20}
                    aria-hidden="true"
                    className="shop-top-menu__row-arrow"
                  />
                </Link>
              )}
              {hasChildren && expanded ? (
                <div
                  className="shop-top-menu__panel"
                  id={`shop-top-menu-panel-${item.key}`}
                >
                  <Link
                    to={item.path}
                    className="shop-top-menu__all-link"
                    onClick={onClose}
                  >{t("مشاهده همهٔ")} {item.label}
                  </Link>
                  <ul className="shop-category-tree">
                    {item.children?.map(function (child) {
                      return (
                        <CategoryBranch
                          key={child.key}
                          item={child}
                          currentPath={currentPath}
                          onClose={onClose}
                        />
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </section>
          );
        })}
      </nav>
    </div>
    </>
  );
}
